import React, { useState, useEffect } from 'react'
import httpHelper from 'utils/http-helper'
import URL_CONSTANTS from 'constants/url-constants'
import { Method_TYPE } from 'utils/http-helper'
import DataTable from 'react-data-table-component'
import ReportHeader from './ReportHeader'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { ArrowUpDown } from 'lucide-react'
import { convertToIST } from 'utils/formtaDate'
import {
  MEMBER_SORT_OPTIONS,
  SEVADAL_SEARCH_FIELDS,
  useFilteredSortedData,
  makeSortableColumn,
  makePlainColumn,
  ReportTableControls,
  ExportButtons,
  createColumnSortHandler,
  reportTableStyles,
} from './reportTableUtils'

export default function BloodGroupReport() {
  const [selectedBloodGroup, setSelectedBloodGroup] = useState('')
  const [sevadalDetails, setSevadalDetails] = useState([])
  const [fullData, setFullData] = useState([])
  const [tableSearchTerm, setTableSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('sector')
  const [sortDirection, setSortDirection] = useState('asc')

  useEffect(() => {
    getSevadalReport()
  }, [])

  const getSevadalReport = async () => {
    try {
      const data = await httpHelper(`${URL_CONSTANTS.GET_SEVADAL_DETAILS}`, Method_TYPE.GET)
      const formattedData =
        data?.reports?.sevaDalList?.map((tableData) => ({
          name: tableData?.Name,
          bloodgroup: tableData?.bloodGroup,
          age: tableData?.age,
          district: tableData?.district,
          samithi: tableData?.samithi,
          mobile: tableData?.mobNo,
          saiconnectedid: tableData?.saiOneId,
          sector: tableData?.Sector,
          deploymentpoint: tableData?.DutyPoint,
        })) || []

      setFullData(formattedData)
      setSevadalDetails(formattedData)
    } catch (err) {
      console.log('getSEvadal report api error', err)
    }
  }

  const getBatchInfo = () => {
    const batchData = JSON.parse(localStorage.getItem('currentBatch'))
    if (batchData) {
      return `${batchData.name}:${convertToIST(batchData.startDate)} - ${convertToIST(batchData.endDate)}`
    }
    return ''
  }

  const handleSearchSevadal = () => {
    let filteredData = [...fullData]
    if (selectedBloodGroup) {
      filteredData = filteredData.filter((item) => item.bloodgroup === selectedBloodGroup)
    }
    setSevadalDetails(filteredData)
  }

  const handleBloodGroupChange = (e) => {
    setSelectedBloodGroup(e.target.value)
  }

  const handleColumnSort = createColumnSortHandler(sortBy, setSortBy, sortDirection, setSortDirection)

  const filteredAndSortedData = useFilteredSortedData(sevadalDetails, {
    searchTerm: tableSearchTerm,
    searchableFields: SEVADAL_SEARCH_FIELDS,
    sortBy,
    sortDirection,
  })

  const columns = [
    {
      name: 'Sl.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
    },
    makeSortableColumn('Name', 'name', sortBy, sortDirection, handleColumnSort),
    makePlainColumn('Blood Group', 'bloodgroup'),
    makeSortableColumn('Age', 'age', sortBy, sortDirection, handleColumnSort),
    makeSortableColumn('District', 'district', sortBy, sortDirection, handleColumnSort),
    makeSortableColumn('Samithi', 'samithi', sortBy, sortDirection, handleColumnSort),
    makePlainColumn('Mobile', 'mobile'),
    makePlainColumn('Sai Connect', 'saiconnectedid'),
    makeSortableColumn('Sector', 'sector', sortBy, sortDirection, handleColumnSort),
    makePlainColumn('Deployment Point', 'deploymentpoint'),
  ]

  const bloodGroups = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'A1B+', 'A1B-']

  const handlePrint = () => {
    const printContent = document.getElementById('print-table').innerHTML
    const printWindow = window.open('', '', 'height=800,width=1000')
    printWindow.document.write('<html><head><title>Blood Group Report</title></head><body>')
    printWindow.document.write(`<h2>TamilNadu Prasanthi Seva - Blood Group Report</h2><p><strong>${getBatchInfo()}</strong></p>`)
    printWindow.document.write(printContent)
    printWindow.document.write('</body></html>')
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
    printWindow.close()
  }

  const handleDownloadPDF = () => {
    const doc = new jsPDF('l')
    doc.text('Blood Group Report', 14, 10)
    doc.text(`${getBatchInfo()}`, 14, 18)
    autoTable(doc, {
      startY: 20,
      head: [
        [
          'Sl.No',
          'Name',
          'Blood Group',
          'Age',
          'District',
          'Samithi',
          'Mobile',
          'Sai Connect',
          'Sector',
          'Deployment Point',
        ],
      ],
      body: filteredAndSortedData.map((row, index) => [
        index + 1,
        row.name,
        row.bloodgroup,
        row.age,
        row.district,
        row.samithi,
        row.mobile,
        row.saiconnectedid,
        row.sector,
        row.deploymentpoint,
      ]),
    })
    doc.save('blood-group-report.pdf')
  }

  const handleDownloadXLS = () => {
    const exportData = filteredAndSortedData.map((item, index) => ({
      'Sl.No': index + 1,
      Name: item.name,
      'Blood Group': item.bloodgroup,
      Age: item.age,
      District: item.district,
      Samithi: item.samithi,
      Mobile: item.mobile,
      'Sai Connect': item.saiconnectedid,
      Sector: item.sector,
      'Deployment Point': item.deploymentpoint,
    }))
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sevadal Report')
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' })
    saveAs(data, 'blood-group-report.xlsx')
  }

  return (
    <>
      <div className="mb-4 flex ">
        <div className="flex flex-wrap items-center gap-6 justify-between w-full">
          <div className="flex items-center gap-2 flex-wrap">
            <label className='font-bold text-sm'>Blood Group:</label>
            <select
              className="p-2 min-w-[200px] rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
              value={selectedBloodGroup}
              onChange={handleBloodGroupChange}
            >
              <option value="">All Blood Groups</option>
              {bloodGroups.map((group, index) => (
                <option key={index} value={group}>
                  {group}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleSearchSevadal}
              className="bg-primary text-primary-foreground text-white px-4 py-2 rounded hover:bg-accent"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      <ReportTableControls
        searchTerm={tableSearchTerm}
        onSearchChange={setTableSearchTerm}
        searchPlaceholder="Search name, age, blood group, district, samithi..."
        sortBy={sortBy}
        onSortByChange={(value) => {
          setSortBy(value)
          setSortDirection('asc')
        }}
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        sortOptions={MEMBER_SORT_OPTIONS}
        totalCount={sevadalDetails.length}
        filteredCount={filteredAndSortedData.length}
        actions={
          <ExportButtons
            show={sevadalDetails.length > 0}
            onPrint={handlePrint}
            onPdf={handleDownloadPDF}
            onXls={handleDownloadXLS}
          />
        }
        hint={
          <>
            Click column headers with{' '}
            <ArrowUpDown size={12} className="inline mx-0.5 text-slate-400" /> to sort by Name, Age, District,
            Samithi, or Sector
          </>
        }
      />

      <ReportHeader header='Blood Group Report' />
      <DataTable
        columns={columns}
        data={filteredAndSortedData}
        fixedHeader
        fixedHeaderScrollHeight='500px'
        customStyles={reportTableStyles}
        className='reports-table'
        noDataComponent={
          <div className="py-10 text-sm text-slate-500">
            {tableSearchTerm.trim() ? 'No rows match your search' : 'No sevadal details available'}
          </div>
        }
      />

      <div id="print-table" style={{ display: 'none' }}>
        <table border="1" cellPadding="5" cellSpacing="0" width="100%">
          <thead>
            <tr>
              <th>Sl.No</th>
              <th>Name</th>
              <th>Blood Group</th>
              <th>Age</th>
              <th>District</th>
              <th>Samithi</th>
              <th>Mobile</th>
              <th>Sai Connect</th>
              <th>Sector</th>
              <th>Deployment Point</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.name}</td>
                <td>{item.bloodgroup}</td>
                <td>{item.age}</td>
                <td>{item.district}</td>
                <td>{item.samithi}</td>
                <td>{item.mobile}</td>
                <td>{item.saiconnectedid}</td>
                <td>{item.sector}</td>
                <td>{item.deploymentpoint}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
